import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const MobilePunchLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        emp_code: '',
        date_from: '',
        date_to: ''
    });
    const [appliedFilters, setAppliedFilters] = useState({
        emp_code: '',
        date_from: '',
        date_to: '',
        search: ''
    });
    const [sortBy, setSortBy] = useState('date_desc');

    const searchInputRef = useRef(null);
    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const params = {};
            if (appliedFilters.emp_code) params.emp_code = appliedFilters.emp_code;
            if (appliedFilters.date_from) params.date_from = appliedFilters.date_from;
            if (appliedFilters.date_to) params.date_to = appliedFilters.date_to;
            if (appliedFilters.search) params.search = appliedFilters.search;

            const response = await axios.get(`${API_BASE}pushattendance/get_mobile_attendance_logs.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: params,
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setLogs(response.data.logs || []);
                setError('');
            } else {
                setLogs([]);
                setError(response.data?.error || 'Failed to fetch mobile attendance logs');
            }
        } catch (error) {
            setLogs([]);
            const errorMsg = error.response?.data?.error || error.message || 'Failed to fetch logs';
            setError(errorMsg);
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, appliedFilters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({
            emp_code: filters.emp_code,
            date_from: filters.date_from,
            date_to: filters.date_to,
            search: searchTerm
        });
    };

    const handleClearFilters = () => {
        setFilters({
            emp_code: '',
            date_from: '',
            date_to: ''
        });
        setSearchTerm('');
        setAppliedFilters({
            emp_code: '',
            date_from: '',
            date_to: '',
            search: ''
        });
        // Keep focus on search input after clearing
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleRefresh = () => {
        fetchLogs();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const sortedLogs = [...logs].sort((a, b) => {
        const dateA = new Date(a.punch_datetime);
        const dateB = new Date(b.punch_datetime);

        if (sortBy === 'date_desc') return dateB - dateA;
        if (sortBy === 'date_asc') return dateA - dateB;
        if (sortBy === 'emp_code') return (a.emp_code || '').localeCompare(b.emp_code || '');
        return dateB - dateA;
    });

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'N/A';
        const date = new Date(dateTime);
        return date.toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const badgeClasses = {
            'IN': 'bg-success',
            'OUT': 'bg-danger',
            'AUTO': 'bg-primary',
            'INVALID': 'bg-warning'
        };
        return badgeClasses[status] || 'bg-secondary';
    };

    if (loading && logs.length === 0) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3 text-muted">Loading mobile punch logs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3">
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/dashboard')} title="Dashboard">
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/Menu')} title="Menu">
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-phone me-2 text-primary"></i>
                                                Mobile Punch Logs
                                            </h2>
                                            <p className="text-muted mb-0">View mobile attendance punch records</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <button className="btn btn-primary" onClick={handleRefresh}>
                                        <i className="bi bi-arrow-clockwise me-2"></i> Refresh
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span><strong>Error:</strong> {error}</span>
                    </div>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setError('')}>
                        Dismiss
                    </button>
                </div>
            )}

            {/* Filters Card */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Search Employee</label>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Employee name or code..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted">Date From</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_from"
                                value={filters.date_from}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small text-muted">Date To</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_to"
                                value={filters.date_to}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small text-muted">Sort By</label>
                            <select
                                className="form-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="date_desc">Latest First</option>
                                <option value="date_asc">Oldest First</option>
                                <option value="emp_code">Employee Code</option>
                            </select>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <div className="d-flex gap-2 w-100">
                                <button
                                    className="btn btn-outline-secondary flex-grow-1"
                                    onClick={handleClearFilters}
                                    title="Clear Filters"
                                >
                                    <i className="bi bi-x-circle"></i> Clear
                                </button>
                                <button
                                    className="btn btn-primary flex-grow-1"
                                    onClick={handleApplyFilters}
                                    title="Apply Filters"
                                >
                                    <i className="bi bi-funnel"></i> Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Filters Display */}
            {(appliedFilters.search || appliedFilters.emp_code || appliedFilters.date_from || appliedFilters.date_to) && (
                <div className="alert alert-info mb-4 py-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <div>
                            <i className="bi bi-funnel-fill me-2"></i>
                            <strong>Active Filters:</strong>
                            {appliedFilters.search && <span className="badge bg-primary ms-2">Search: {appliedFilters.search}</span>}
                            {appliedFilters.emp_code && <span className="badge bg-secondary ms-2">Emp Code: {appliedFilters.emp_code}</span>}
                            {appliedFilters.date_from && <span className="badge bg-secondary ms-2">From: {appliedFilters.date_from}</span>}
                            {appliedFilters.date_to && <span className="badge bg-secondary ms-2">To: {appliedFilters.date_to}</span>}
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                            Clear All
                        </button>
                    </div>
                </div>
            )}

            {/* Results Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4" style={{ width: '80px' }}>Log ID</th>
                                    <th>Employee</th>
                                    <th style={{ width: '180px' }}>Date Time</th>
                                    <th style={{ width: '80px' }}>Type</th>
                                    <th style={{ width: '150px' }}>Location</th>
                                    <th style={{ width: '120px' }}>Verification</th>
                                    <th>Device Info</th>
                                    <th className="text-end pe-4" style={{ width: '100px' }}>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5">
                                            <i className="bi bi-inbox fs-1 text-muted"></i>
                                            <p className="text-muted mt-2 mb-0">No logs found</p>
                                        </td>
                                    </tr>
                                ) : (
                                    sortedLogs.map(log => (
                                        <tr key={log.log_id}>
                                            <td className="ps-4 fw-semibold">#{log.log_id}</td>
                                            <td>
                                                <div className="fw-semibold">{log.emp_code || `EMP-${log.emp_id}`}</div>
                                                <small className="text-muted">{log.emp_name}</small>
                                            </td>
                                            <td>
                                                <div className="fw-semibold">{formatDateTime(log.punch_datetime)}</div>
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(log.punch_type)}`}>{log.punch_type}</span>
                                            </td>
                                            <td>
                                                <small>
                                                    Lat: {log.latitude}<br />
                                                    Lng: {log.longitude}
                                                </small>
                                            </td>
                                            <td>
                                                <div>Face Score: {log.face_score}</div>
                                                <span className={`badge ${log.face_verified ? 'bg-success' : 'bg-danger'}`}>
                                                    {log.face_verified ? 'Verified' : 'Unverified'}
                                                </span>
                                            </td>
                                            <td><small className="text-muted">{log.device_info}</small></td>
                                            <td className="text-end pe-4 text-muted">{log.remarks || '-'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Summary */}
            {sortedLogs.length > 0 && (
                <div className="mt-3 text-end text-muted small">
                    Showing {sortedLogs.length} log(s)
                </div>
            )}
        </div>
    );
};

export default MobilePunchLogs;